import { Button, ToastContainer } from '@bfe/moly';
import { useToggle } from 'ahooks';
import { Card } from 'antd';
import classNames from 'classnames';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';

import ApiAuthModal, { ApiAuthScene } from '@/components/ApiAuthModal';
import ComplianceElement from '@/components/ComplianceElement';
import { Google2faModal } from '@/components/Google2faModal';
import ResultModal from '@/components/ResultModal';
import { refreshSearchTable } from '@/components/SearchTablePage/utils';
import { BybitSearchListRef } from '@/components/SearchTablePage/utils/commonInterface';
import useEscrowAccount from '@/hooks/useEscrowAccount';
import { useMemberInfo } from '@/hooks/useMemberInfo';
import useUpgradeUnifiedAccount from '@/hooks/useUpgradeUnifiedAccount';
import { useI18nContext } from '@/i18n/I18n';
import { IApiManagementRecord } from '@/services/types/apiManagement';
import { UserInfo } from '@/services/types/common';
import { useStore } from '@/store/useStore';
import { useUserStore } from '@/store/useUser';
import {
  navigateToMainAccountCreateApiPage,
  navigateToSubAccountCreateApiPage,
} from '@/utils/navigateTo';
import { $tracing } from '@/utils/tracing';

import { SubAccountApiMgtBreadcrumb } from './comps/Banner';
import { FirstTipModal } from './comps/FirstTipModal';
import { SelectApiTypeModal } from './comps/selectApiTypeModal';
import styles from './index.module.less';

const TechopsSupport = lazy(() => import('./comps/TechopsSupport'));

const ApiAlert = lazy(() => import('./comps/Alert'));

const UpgradeApi = lazy(() => import('./UpgradeApi/upgradeApi'));

const ApiManagementList = lazy(
  () => import('./comps/ApiManagementList/ApiManagementList'),
);

const ApiManagement = () => {
  const [showGoogle2faModal, { toggle }] = useToggle(false);
  const searchListRef = useRef<BybitSearchListRef<IApiManagementRecord>>();
  const refreshTable = () => {
    refreshSearchTable({}, searchListRef);
  };
  const [$t] = useI18nContext();
  const {
    userInfo,
    userInfoLoading: loading,
    userRole: { isDemoAccount, updateUserProfile },
  } = useStore();
  const [search] = useSearchParams();
  const subAccountId = search.get('subaccount_id');
  const name = search.get('name');
  const memberType = search.get('memberType');
  const memberStatus = search.get('memberStatus') || '';
  const subAccountMemberTag = search.get('member_tag') || '';
  const [visibleTypeModal, setVisibleTypeModal] = useState(false);
  const [isGray, setIsGray] = useState(false);
  const { memberInfo } = useMemberInfo();

  const { complianceConfig } = useUserStore();

  const isMainAccountManagementSubAccountApi = search.has('subaccount_id');

  const [isCopperSubAccount, setIsCopperSubAccount] = useState(false);

  const {
    accountTypeName,
    institutionInfoCode,
    authName,
    needApiAuth,
    isFirstCreateApi,
  } = useEscrowAccount();
  const [visibleAuthModal, setVisibleAuthModal] = useState(false);

  const { toggleUpgradeUnifiedAccount, needShowUpgradeUnifiedAccount } =
    useUpgradeUnifiedAccount('apiManagement');

  const [showSubAccountUpgradeTipModal, setShowSubAccountUpgradeTipModal] =
    useState(false);

  useEffect(() => {
    setIsGray(memberInfo.rsa === 1);
  }, [memberInfo]);

  const subAccount = useMemo(() => {
    return (userInfo as UserInfo).is_submember
      ? (userInfo as UserInfo)?.id
      : subAccountId;
  }, [userInfo, subAccountId]);

  useEffect(() => {
    refreshTable();
  }, [subAccountId]);

  const hasGoogle2fa = () => {
    if (!(userInfo as UserInfo)?.has_google2fa && !isDemoAccount) {
      toggle();
      return false;
    }
    return true;
  };

  // 如果是子账号列表页，根据api_info接口判断子账号标签并补充到url的member_tag参数中
  const setSubAccountCopperTag = useCallback(
    (res: any) => {
      if (isMainAccountManagementSubAccountApi) {
        const isCopperSubAccount = [
          'clearloop-sub',
          'fireblocks-sub',
          'bycustody',
          'zodia',
        ].includes(res?.register_tag);
        setIsCopperSubAccount(isCopperSubAccount);
      }
    },
    [isMainAccountManagementSubAccountApi],
  );

  const handleCreateBtn = () => {
    if (needShowUpgradeUnifiedAccount) {
      // 如果是母账号给子账号创建API
      // 给用户弹窗提示切换到子账号并完成升级后再创建
      if (subAccountId) {
        setShowSubAccountUpgradeTipModal(true);
        return;
      }

      toggleUpgradeUnifiedAccount(true, {
        close: (status) => {
          updateUserProfile();
          if (status !== '') {
            createNewKey();
          }
        },
      });
      return;
    }
    createNewKey();
  };

  const createNewKey = () => {
    if (!hasGoogle2fa()) {
      return;
    }
    // 仅针对母账号判断是否需要授权弹窗
    if (needApiAuth) {
      setVisibleAuthModal(true);
      return;
    }
    if (isFirstCreateApi) {
      onCreate(institutionInfoCode as string);
      return;
    }
    if (isGray) {
      setVisibleTypeModal(true);
    } else {
      onCreate('system');
    }
  };

  const onCreate = (type: 'system' | 'auto' | string) => {
    if (subAccountId) {
      navigateToSubAccountCreateApiPage({
        subAccountMemberTag: isCopperSubAccount
          ? `${subAccountMemberTag},copper`
          : subAccountMemberTag,
        subAccountId: `${subAccount || 0}`,
        subAccountLoginName: name as string,
        subAccountMemberType: memberType as any,
        subAccountMemberStatus: memberStatus,
        type,
        isToEdit: false,
      });
    } else {
      navigateToMainAccountCreateApiPage({
        type,
        isToEdit: false,
      });
    }
    $tracing.addEvent({
      t: 'action',
      c: 15104,
    });
  };

  const ApiHeaderOperation = () => {
    return (
      <div className={classNames('api-management-title')}>
        <div className="api-management-title-text">
          <span data-paint>{$t('APIManagement')}</span>
          {name && <> ({name})</>}
        </div>
        <div>
          <Button
            variant="primary"
            size="x-small"
            onClick={handleCreateBtn}
            loading={loading}
          >
            {$t('Create New Key')}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <ToastContainer />
      <div
        className={classNames('api-management-container', {
          'subaccount-api-management-container': subAccountId,
        })}
      >
        <Suspense fallback="">
          <ApiAlert />
        </Suspense>

        <Suspense fallback="">
          <ComplianceElement config={complianceConfig?.UpgradeToApiPro}>
            {!subAccountId && !isDemoAccount ? <UpgradeApi /> : null}
          </ComplianceElement>
        </Suspense>

        {!!subAccountId && <SubAccountApiMgtBreadcrumb />}

        {!subAccountId && <ApiHeaderOperation />}

        <FirstTipModal />
        <Google2faModal
          onCancel={toggle}
          onOk={toggle}
          showGoogle2faModal={showGoogle2faModal}
        />

        <div className="subaccount-api-management-body">
          <Card bordered={false}>
            {subAccountId && <ApiHeaderOperation />}

            <ul className={styles.bybitApiTip}>
              <li>{$t('AddApi.p1')}</li>
              <li>{$t('AddApi.p11')}</li>
              <li>
                {$t('AddApi.p3')}
                {$t('AddApi.p4')}192.168.1.1,192.168.1.2,192.168.1.3
              </li>
              <li className={styles.warn}>{$t('AddApi.p12')}</li>
              <li>{$t('AddApi.p10')}</li>
            </ul>
            <Suspense fallback="">
              <TechopsSupport />
            </Suspense>
          </Card>

          <Suspense fallback="">
            <ApiManagementList
              subAccountId={subAccount}
              isFromSubAccount={!!subAccountId}
              searchListRef={searchListRef}
              refreshTable={refreshTable}
              hasGoogle2fa={hasGoogle2fa}
              isSubAccountLogin={(userInfo as UserInfo).is_submember}
              loading={loading}
              isCopperSubAccount={isCopperSubAccount}
              apiRequestCallback={(res: any) => {
                setSubAccountCopperTag(res);
              }}
              memberType={memberType}
            />
          </Suspense>
        </div>

        <SelectApiTypeModal
          visible={visibleTypeModal}
          onCreate={onCreate}
          onCancel={() => {
            setVisibleTypeModal(false);
          }}
        />
        {visibleAuthModal && (
          <ApiAuthModal
            to={authName}
            from={ApiAuthScene['openApi']}
            text={$t('apiAuthModal.copper.textNew').replaceAll(
              '{{name}}',
              $t(accountTypeName),
            )}
            accountTypeName={accountTypeName}
            onSuccess={() => {
              onCreate(institutionInfoCode as string);
            }}
            onCancel={() => {
              setVisibleAuthModal(false);
            }}
          />
        )}
      </div>
      <ResultModal
        modalProps={{
          open: showSubAccountUpgradeTipModal,
          size: 'small',
          cancelText: false,
          okText: $t('switchSubAccountAndUpgrade'),
          onOk: () => {
            setShowSubAccountUpgradeTipModal(false);
          },
          onClose: () => {
            setShowSubAccountUpgradeTipModal(false);
          },
        }}
        resultProps={{
          subTitle: $t('subAccountNotUpgradedCannotCreateNewApi'),
          status: 'warning',
        }}
      />
    </>
  );
};
export default ApiManagement;
